import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import ModelsForm from "../../components/ui/ModelsForm";
import MantenimientoModal from "../../components/ui/MantenimientoForm";
import { toast } from "react-toastify";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";
import { useBulkSelection } from "../../hooks/useBulkSelection";
import { useAiMasivoJob } from "../../hooks/useAiMasivoJob";

const ModelosPage = () => {
  const [activeTab, setActiveTab] = useState("modelos");

  const [modelos, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredModels, setFilteredModels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedModelo, setSelectedModelo] = useState(null);
  const [showMantenimientoModal, setShowMantenimientoModal] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  const [marcas, setMarcas] = useState([]);
  const [loadingMarcas, setLoadingMarcas] = useState(false);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/models";
  const MARCAS_API_URL = process.env.REACT_APP_API_URL + "/marcas";
  const planActual = localStorage.getItem("planActual") || "FREE";
  const isFreePlan = planActual === "FREE";

  const canDeleteModelos = hasPermission("modelos.eliminar");

  const {
    job: hojaVidaJob,
    enProceso: generandoMasivo,
    iniciar: iniciarHojaVidaMasivo,
  } = useAiMasivoJob({
    tipo: "hoja-vida-masivo",
    endpoint: "/hojavida/generar-masivo",
    onCompleted: (job) => {
      fetchModels();
      if (job.estado === "completado") {
        const p = job.progreso || {};
        toast.success(
          `Hojas de vida: ${p.generadosIA || 0} con IA, ${p.reusadosOtraEmpresa || 0} reusadas, ${p.fallidos || 0} fallidas. Revisa antes de usar.`,
        );
      } else {
        toast.error(job.error || "La generación masiva de hoja de vida falló");
      }
    },
  });

  const fetchModels = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModels(res.data);
      setFilteredModels(res.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarcas = async () => {
    setLoadingMarcas(true);
    try {
      const res = await axios.get(MARCAS_API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMarcas(res.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoadingMarcas(false);
    }
  };

  useEffect(() => {
    fetchModels();
    fetchMarcas();
    // eslint-disable-next-line
  }, []);

  // Filtro de búsqueda
  useEffect(() => {
    const term = search.toLowerCase();
    const filtered = modelos.filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        m.marca?.name?.toLowerCase().includes(term),
    );
    setFilteredModels(filtered);
    setCurrentPage(1);
  }, [search, modelos]);

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredModels.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredModels.length / itemsPerPage);

  const visibleModelIds = currentItems.map((c) => c._id);
  const {
    selectedIds: selectedModelIds,
    toggleSelect: toggleSelectModel,
    toggleSelectAll: toggleSelectAllModels,
    allSelected: allModelsSelected,
    clearSelection: clearModelSelection,
    hasSelection: hasModelSelection,
  } = useBulkSelection(visibleModelIds);

  const modelosPorMarca = useMemo(() => {
    const map = {};
    modelos.forEach((m) => {
      const marcaId = m.marca?._id;
      if (!marcaId) return;
      map[marcaId] = (map[marcaId] || 0) + 1;
    });
    return map;
  }, [modelos]);

  const visibleMarcaIds = marcas.map((m) => m._id);
  const {
    selectedIds: selectedMarcaIds,
    toggleSelect: toggleSelectMarca,
    toggleSelectAll: toggleSelectAllMarcas,
    allSelected: allMarcasSelected,
    clearSelection: clearMarcaSelection,
    hasSelection: hasMarcaSelection,
  } = useBulkSelection(visibleMarcaIds);

  const handleEdit = (model) => {
    setSelectedModelo(model);
    setShowModal(true);
  };

  const handleConfig = (model) => {
    setShowMantenimientoModal(model);
  };

  const handleCreate = () => {
    setSelectedModelo(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedModelo) {
        await axios.put(`${API_URL}/${selectedModelo._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Modelo actualizado correctamente ✅");
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Modelo creado correctamente ✅");
      }

      setShowModal(false);
      fetchModels();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Ocurrió un error inesperado al guardar el modelo";
      toast.error(backendMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este modelo?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Modelo eliminado correctamente ✅");
      fetchModels();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Error al eliminar modelo";
      toast.error(backendMessage);
    }
  };

  const handleBulkDeleteModelos = async () => {
    if (
      !window.confirm(`¿Eliminar ${selectedModelIds.size} modelo(s) seleccionado(s)?`)
    )
      return;
    try {
      const res = await axios.delete(`${API_URL}/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: Array.from(selectedModelIds) },
      });
      if (res.data.deleted === 0) {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      clearModelSelection();
      fetchModels();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Error al eliminar modelos seleccionados";
      toast.error(backendMessage);
    }
  };

  const handleDeleteMarca = async (id) => {
    if (!window.confirm("¿Eliminar esta marca?")) return;
    try {
      await axios.delete(`${MARCAS_API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Marca eliminada correctamente ✅");
      fetchMarcas();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Error al eliminar marca";
      toast.error(backendMessage);
    }
  };

  const handleBulkDeleteMarcas = async () => {
    if (
      !window.confirm(`¿Eliminar ${selectedMarcaIds.size} marca(s) seleccionada(s)?`)
    )
      return;
    try {
      const res = await axios.delete(`${MARCAS_API_URL}/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: Array.from(selectedMarcaIds) },
      });
      if (res.data.deleted === 0) {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      clearMarcaSelection();
      fetchMarcas();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Error al eliminar marcas seleccionadas";
      toast.error(backendMessage);
    }
  };

  const handleImport = async () => {
    if (
      !window.confirm("¿Deseas importar todas las marcas y modelos iniciales?")
    )
      return;

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/models/import`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Importación completada ✅");
      fetchModels();
      fetchMarcas();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Ocurrió un error inesperado al importar";
      toast.error(backendMessage);
    }
  };

  const handleGenerarHojaVidaMasivo = async () => {
    if (isFreePlan || generandoMasivo) return;
    if (
      !window.confirm(
        "¿Generar con IA la hoja de vida de todos los modelos pendientes? Se te notificará al terminar.",
      )
    )
      return;

    const resultado = await iniciarHojaVidaMasivo();
    if (resultado.duplicate) toast.info(resultado.message);
    else if (resultado.ok) toast.success(resultado.message);
    else toast.error(resultado.message);
  };

  const progresoHojaVidaTexto = generandoMasivo
    ? `Generando... ${hojaVidaJob?.progreso?.procesados ?? 0}/${hojaVidaJob?.progreso?.total ?? 0}`
    : "Generar todas con IA";

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex gap-2 mb-4 border-b">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "modelos"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("modelos")}
          >
            Modelos
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "marcas"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("marcas")}
          >
            Marcas
          </button>
        </div>

        {activeTab === "modelos" && (
          <>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
              <h2 className="text-lg font-semibold">Modelos</h2>
              <div className="flex gap-2 items-center w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Buscar modelo o marca..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                {hasPermission("modelos.hojadevida") && (
                  <div className="relative inline-block">
                    <Button
                      variant="outline"
                      onClick={handleGenerarHojaVidaMasivo}
                      disabled={isFreePlan || generandoMasivo}
                      className={
                        isFreePlan ? "bg-gray-300 text-gray-600 cursor-not-allowed" : ""
                      }
                    >
                      {generandoMasivo && (
                        <LoaderPinwheel className="h-4 w-4 mr-1 animate-spin inline" />
                      )}
                      {progresoHojaVidaTexto}
                    </Button>
                    {isFreePlan && (
                      <span className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full shadow">
                        Solo planes de pago
                      </span>
                    )}
                  </div>
                )}
                {hasPermission("modelos.crear") && (
                  <Button onClick={handleCreate}>+ Nuevo modelo</Button>
                )}
              </div>
            </div>

            {!loading && modelos.length === 0 && (
              <Button onClick={handleImport} className="ml-2">
                Importar marcas y modelos
              </Button>
            )}

            {loading && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
                Cargando datos, por favor espera...
              </div>
            )}

            <div className="flex items-center justify-end mb-2">
              <PageSizeSelect
                value={itemsPerPage}
                onChange={(size) => {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                }}
              />
            </div>

            {hasModelSelection && canDeleteModelos && (
              <div className="flex items-center justify-between mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <span className="text-sm text-red-700">
                  {selectedModelIds.size} modelo(s) seleccionado(s)
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearModelSelection}>
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="bg-red-500 hover:bg-red-600 text-white"
                    onClick={handleBulkDeleteModelos}
                  >
                    Eliminar seleccionados
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-sm">
                <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
                  <tr>
                    {canDeleteModelos && (
                      <th className="py-2 px-4 text-left">
                        <input
                          type="checkbox"
                          checked={allModelsSelected}
                          onChange={toggleSelectAllModels}
                        />
                      </th>
                    )}
                    <th className="py-2 px-4 text-left">Categoría</th>
                    <th className="py-2 px-4 text-left">Marca</th>
                    <th className="py-2 px-4 text-left">Modelo</th>
                    <th className="py-2 px-4 text-left">Fabricante</th>
                    <th className="py-2 px-4 text-left">Imagen</th>
                    <th className="py-2 px-4 text-center">Hoja de vida</th>
                    <th className="py-2 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((c) => (
                    <tr
                      key={c._id}
                      className={
                        c.conEquipos
                          ? "border-b hover:bg-gray-50 text-green-600"
                          : "border-b hover:bg-gray-50"
                      }
                    >
                      {canDeleteModelos && (
                        <td className="py-2 px-4">
                          <input
                            type="checkbox"
                            checked={selectedModelIds.has(c._id)}
                            onChange={() => toggleSelectModel(c._id)}
                          />
                        </td>
                      )}
                      <td className="py-2 px-4">
                        {c.categoria?.name || "No registra"}
                      </td>
                      <td className="py-2 px-4">{c.marca?.name || "Sin marca"}</td>
                      <td className="py-2 px-4">{c.name}</td>
                      <td className="py-2 px-4">{c.fabricante || "No registra"}</td>
                      <td className="py-2 px-4">
                        {c.img ? (
                          <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                            Registrada
                          </span>
                        ) : (
                          <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-full">
                            Sin imagen
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-center">
                        {c.tieneMantenimientoEstandar ? (
                          <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                            Registrada
                          </span>
                        ) : (
                          <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-full">
                            Sin hoja de vida
                          </span>
                        )}
                        {c.tieneMantenimientoEstandar && c.mantenimientoRevisado === false && (
                          <div className="mt-1">
                            <span className="bg-yellow-100 text-yellow-800 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                              ⚠️ Generado por IA — revisar antes de usar
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right">
                        {hasPermission("modelos.hojadevida") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleConfig(c)}
                          >
                            Configurar
                          </Button>
                        )}
                        {hasPermission("modelos.editar") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2"
                            onClick={() => handleEdit(c)}
                          >
                            Editar
                          </Button>
                        )}
                        {hasPermission("modelos.eliminar") && (
                          <Button
                            variant="destructive"
                            size="sm"
                            className="ml-2 bg-red-500 hover:bg-red-600 text-white"
                            onClick={() => handleDelete(c._id)}
                          >
                            Eliminar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}

                  {currentItems.length === 0 && (
                    <tr>
                      <td
                        colSpan={canDeleteModelos ? "8" : "7"}
                        className="text-center py-4 text-gray-500 italic"
                      >
                        No se encontraron modelos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600 gap-3">
                <span>
                  Mostrando {currentItems.length} de {filteredModels.length}{" "}
                  resultados
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    ← Anterior
                  </Button>
                  <span className="px-2 py-1 bg-gray-100 rounded-md">
                    Página {currentPage} de {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Siguiente →
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "marcas" && (
          <>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
              <h2 className="text-lg font-semibold">Marcas</h2>
            </div>

            {loadingMarcas && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
                Cargando datos, por favor espera...
              </div>
            )}

            {hasMarcaSelection && canDeleteModelos && (
              <div className="flex items-center justify-between mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <span className="text-sm text-red-700">
                  {selectedMarcaIds.size} marca(s) seleccionada(s)
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearMarcaSelection}>
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="bg-red-500 hover:bg-red-600 text-white"
                    onClick={handleBulkDeleteMarcas}
                  >
                    Eliminar seleccionadas
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-sm">
                <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
                  <tr>
                    {canDeleteModelos && (
                      <th className="py-2 px-4 text-left">
                        <input
                          type="checkbox"
                          checked={allMarcasSelected}
                          onChange={toggleSelectAllMarcas}
                        />
                      </th>
                    )}
                    <th className="py-2 px-4 text-left">Nombre</th>
                    <th className="py-2 px-4 text-center">Modelos asociados</th>
                    <th className="py-2 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {marcas.map((m) => (
                    <tr key={m._id} className="border-b hover:bg-gray-50">
                      {canDeleteModelos && (
                        <td className="py-2 px-4">
                          <input
                            type="checkbox"
                            checked={selectedMarcaIds.has(m._id)}
                            onChange={() => toggleSelectMarca(m._id)}
                          />
                        </td>
                      )}
                      <td className="py-2 px-4">{m.name}</td>
                      <td className="py-2 px-4 text-center">
                        {modelosPorMarca[m._id] || 0}
                      </td>
                      <td className="py-2 px-4 text-right">
                        {hasPermission("modelos.eliminar") && (
                          <Button
                            variant="destructive"
                            size="sm"
                            className="bg-red-500 hover:bg-red-600 text-white"
                            onClick={() => handleDeleteMarca(m._id)}
                          >
                            Eliminar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}

                  {marcas.length === 0 && !loadingMarcas && (
                    <tr>
                      <td
                        colSpan={canDeleteModelos ? "4" : "3"}
                        className="text-center py-4 text-gray-500 italic"
                      >
                        No se encontraron marcas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {showModal && (
          <ModelsForm
            model={selectedModelo}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
          />
        )}

        {showMantenimientoModal && (
          <MantenimientoModal
            marcaId={showMantenimientoModal.marca?._id}
            categoriaId={showMantenimientoModal.categoria?._id}
            modeloId={showMantenimientoModal._id}
            onClose={() => setShowMantenimientoModal(false)}
            onSaved={fetchModels}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ModelosPage;
